/**
 * Workflow Validation Logic
 *
 * Validates workflow definitions for structural correctness, step configuration,
 * dependency integrity, circular dependency detection, and parameter completeness.
 * Returns categorized errors and warnings with actionable resolution guidance.
 */
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Run all validation checks on a workflow definition.
 */
export function validateWorkflow(workflow, stepTemplates) {
    const errors = [];
    const warnings = [];
    validateStructure(workflow, errors);
    for (const step of workflow.steps) {
        validateStep(step, workflow, errors, warnings);
    }
    validateDependencies(workflow, errors);
    checkCircularDependencies(workflow, errors);
    if (stepTemplates) {
        for (const step of workflow.steps) {
            validateParameters(step, stepTemplates, errors, warnings);
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}
// ---------------------------------------------------------------------------
// Structural validation
// ---------------------------------------------------------------------------
/**
 * Validate top-level workflow structure: name required, at least one step.
 */
export function validateStructure(workflow, errors) {
    if (!workflow.name || workflow.name.trim() === '') {
        errors.push({
            severity: 'error',
            message: 'Workflow name is required',
            resolution: 'Provide a descriptive name for the workflow',
        });
    }
    if (!workflow.steps || workflow.steps.length === 0) {
        errors.push({
            severity: 'error',
            message: 'Workflow must contain at least one step',
            resolution: 'Add steps from the step library to the canvas',
        });
    }
}
// ---------------------------------------------------------------------------
// Step validation
// ---------------------------------------------------------------------------
/**
 * Validate an individual step: required fields per step type, timeout, and
 * that all declared dependencies reference existing steps.
 */
export function validateStep(step, workflow, errors, warnings) {
    if (!step.id) {
        errors.push({
            severity: 'error',
            stepId: step.id,
            message: 'Step ID is required',
            resolution: 'System error — step should have an auto-generated ID',
        });
    }
    if (step.type === 'agent_task' && !step.agent) {
        errors.push({
            severity: 'error',
            stepId: step.id,
            field: 'agent',
            message: 'Agent task must specify an agent',
            resolution: 'Select an agent from the configuration panel',
        });
    }
    if (step.type === 'function' && !step.function) {
        errors.push({
            severity: 'error',
            stepId: step.id,
            field: 'function',
            message: 'Function step must specify a function name',
            resolution: 'Provide a function name in the configuration panel',
        });
    }
    if (step.timeout_seconds <= 0) {
        warnings.push({
            severity: 'warning',
            stepId: step.id,
            field: 'timeout_seconds',
            message: 'Step timeout should be positive',
            resolution: 'Set a reasonable timeout value (e.g., 300 seconds)',
        });
    }
    for (const depId of step.depends_on) {
        const depExists = workflow.steps.some((s) => s.id === depId);
        if (!depExists) {
            errors.push({
                severity: 'error',
                stepId: step.id,
                field: 'depends_on',
                message: `Dependency step '${depId}' does not exist`,
                resolution: 'Remove invalid dependency or add the missing step',
            });
        }
    }
}
// ---------------------------------------------------------------------------
// Dependency validation — orphaned / unreachable steps
// ---------------------------------------------------------------------------
/**
 * Verify all dependency references exist and detect orphaned steps that are
 * not reachable from any root step (a step with no dependencies).
 */
export function validateDependencies(workflow, errors) {
    const reachable = findReachableSteps(workflow);
    for (const step of workflow.steps) {
        if (!reachable.has(step.id)) {
            errors.push({
                severity: 'error',
                stepId: step.id,
                message: 'Step is not reachable in workflow execution',
                resolution: 'Connect this step to the workflow or remove it',
            });
        }
    }
}
/**
 * Find all steps reachable from root steps (steps with no dependencies).
 * Traverses forward through the dependency graph.
 */
function findReachableSteps(workflow) {
    const reachable = new Set();
    const rootSteps = workflow.steps.filter((s) => s.depends_on.length === 0);
    const visit = (stepId) => {
        if (reachable.has(stepId))
            return;
        reachable.add(stepId);
        // Find steps that depend on this step and visit them
        for (const s of workflow.steps) {
            if (s.depends_on.includes(stepId)) {
                visit(s.id);
            }
        }
    };
    for (const root of rootSteps) {
        visit(root.id);
    }
    return reachable;
}
// ---------------------------------------------------------------------------
// Circular dependency detection (DFS)
// ---------------------------------------------------------------------------
/**
 * DFS-based cycle detection in the step dependency graph.
 * Reports an error if any cycle is found.
 */
export function checkCircularDependencies(workflow, errors) {
    const visited = new Set();
    const recursionStack = new Set();
    const hasCycle = (stepId) => {
        visited.add(stepId);
        recursionStack.add(stepId);
        const step = workflow.steps.find((s) => s.id === stepId);
        if (!step)
            return false;
        for (const depId of step.depends_on) {
            if (!visited.has(depId)) {
                if (hasCycle(depId))
                    return true;
            }
            else if (recursionStack.has(depId)) {
                return true;
            }
        }
        recursionStack.delete(stepId);
        return false;
    };
    for (const step of workflow.steps) {
        if (!visited.has(step.id)) {
            if (hasCycle(step.id)) {
                errors.push({
                    severity: 'error',
                    message: 'Workflow contains circular dependencies',
                    resolution: 'Remove circular dependencies between steps. Each step should only depend on steps that come before it.',
                });
                break;
            }
        }
    }
}
// ---------------------------------------------------------------------------
// Parameter validation against step templates
// ---------------------------------------------------------------------------
/**
 * Check that all required parameters for a step are configured, based on
 * the matching step template's ParameterDefinition list.
 */
export function validateParameters(step, stepTemplates, errors, warnings) {
    // Match step to its template by type + function/agent name
    const template = findTemplateForStep(step, stepTemplates);
    if (!template) {
        // No matching template — nothing to validate against
        return;
    }
    const stepConfig = (typeof step.input === 'object' && step.input !== null ? step.input : {});
    for (const param of template.parameters) {
        const value = stepConfig[param.name];
        if (param.required && (value === undefined || value === null || value === '')) {
            errors.push({
                severity: 'error',
                stepId: step.id,
                field: param.name,
                message: `Required parameter '${param.name}' is missing`,
                resolution: `Fill in the '${param.name}' parameter in the configuration panel`,
            });
            continue;
        }
        // Skip further checks if value is absent and not required
        if (value === undefined || value === null)
            continue;
        validateParameterValue(param, value, step.id, errors, warnings);
    }
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function findTemplateForStep(step, templates) {
    // Try matching by function name or agent name first, then fall back to type
    return templates.find((t) => {
        if (step.type === 'function' && t.type === 'function') {
            return t.name === step.function || t.id === step.function;
        }
        if (step.type === 'agent_task' && t.type === 'agent_task') {
            return t.name === step.agent || t.id === step.agent;
        }
        return false;
    });
}
function validateParameterValue(param, value, stepId, errors, warnings) {
    const { validation } = param;
    if (!validation)
        return;
    if (param.type === 'number' && typeof value === 'number') {
        if (validation.min !== undefined && value < validation.min) {
            errors.push({
                severity: 'error',
                stepId,
                field: param.name,
                message: `Parameter '${param.name}' must be at least ${validation.min}`,
                resolution: `Set '${param.name}' to a value >= ${validation.min}`,
            });
        }
        if (validation.max !== undefined && value > validation.max) {
            errors.push({
                severity: 'error',
                stepId,
                field: param.name,
                message: `Parameter '${param.name}' must be at most ${validation.max}`,
                resolution: `Set '${param.name}' to a value <= ${validation.max}`,
            });
        }
    }
    if (param.type === 'text' && typeof value === 'string' && validation.pattern) {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(value)) {
            warnings.push({
                severity: 'warning',
                stepId,
                field: param.name,
                message: `Parameter '${param.name}' does not match expected pattern`,
                resolution: `Ensure '${param.name}' matches the pattern: ${validation.pattern}`,
            });
        }
    }
}
